import { useEffect, useState } from "react";
import "./ProductOverview.scss";
import Carousel from "react-bootstrap/Carousel";
import Optional from "./components/Optional";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
export default function ProductOverview({
  product,
  cart,
  setCart,
  setShowMessage,
  isCombo,
  showAllId,
  setShowAllId,
  isActive,
  setActive,
}) {
  const { categoryId, subcategoryId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [amount, setAmount] = useState(1);
  const [price, setPrice] = useState(null);
  const [addToCart, setAddToCart] = useState({});
  const [oldPrice, setOldPrice] = useState();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isCategoryValid, setIsCategoryValid] = useState(false);
  const [weightDropdown, setWeightDropdown] = useState(false);
  const [selectedWeight, setSelectedWeight] = useState(null);
  const [weightOptions, setWeightOptions] = useState([]);
  const [priceBGN, setPriceBGN] = useState(null);
  const removableProducts =
    product?.products?.filter(
      (prod, index, products) =>
        prod.removable &&
        products.findIndex((existingProd) => existingProd.id === prod.id) ===
          index,
    ) || [];

  const hasWeightOptions = weightOptions?.length > 0;

  const baseUnitPrice = isCombo
    ? Number(product?.price)
    : product?.promoPrice !== null && product?.promoPrice !== undefined
      ? Number(product?.promoPrice)
      : Number(product?.price);

  // `price` also carries the optional extras the user picked. Those are flat
  // add-ons, so they must not be multiplied by the chosen weight.
  const extrasPrice =
    price === null || price === undefined || Number.isNaN(Number(price))
      ? 0
      : Number(price) - baseUnitPrice;

  // Prices of meals sold by weight are per 100g.
  const applyChosenWeight = (value) =>
    hasWeightOptions && selectedWeight
      ? (Number(value) * Number(selectedWeight)) / 100
      : Number(value);

  // Kept at full precision - like everywhere else in the app, the rounding
  // happens only when the price is rendered.
  const cartUnitPrice = applyChosenWeight(baseUnitPrice) + extrasPrice;

  const isSameCartItem = (item, other) =>
    item?.id === other?.id &&
    (item?.chosenWeight ?? null) === (other?.chosenWeight ?? null) &&
    JSON.stringify(item?.added ?? []) === JSON.stringify(other?.added ?? []) &&
    JSON.stringify(item?.removed ?? []) === JSON.stringify(other?.removed ?? []);

  useEffect(() => {
    if (Number.isNaN(cartUnitPrice)) {
      setPriceBGN(null);
      return;
    }

    setPriceBGN((cartUnitPrice * 1.95583).toFixed(2));
  }, [cartUnitPrice]);

  useEffect(() => {
    if (isActive) {
      setIsCategoryValid(true);
    } else {
      setIsCategoryValid(false);
    }
  }, [isActive]);

  const generateWeightRange = (minWeight, maxWeight, increment) => {
    if (!increment || increment <= 0 || !maxWeight || maxWeight <= 0) {
      return [];
    }
    // Meals can be set up with a maximum weight only - then the smallest
    // portion that can be ordered is one increment.
    const firstWeight = minWeight > 0 ? minWeight : increment;
    const weights = [];
    for (let weight = firstWeight; weight <= maxWeight; weight += increment) {
      weights.push(weight);
    }
    return weights;
  };

  useEffect(() => {
    const options =
      product?.weight === null || product?.weight === undefined
        ? generateWeightRange(
            product?.minWeight,
            product?.maxWeight,
            product?.increment,
          )
        : [];

    setWeightOptions(options);
    // Always keep a valid weight selected, so the price shown and the price
    // sent to the cart can never fall back to the raw per-100g price.
    setSelectedWeight(options.length > 0 ? options[0] : null);
  }, [product]);

  useEffect(() => {
    if (!product) return;

    if (!isCombo) {
      const nextPrice =
        product?.promoPrice !== null
          ? Number(product?.promoPrice)
          : Number(product?.price);

      setOldPrice(product?.promoPrice !== null ? product?.price : undefined);
      setPrice(nextPrice);
      setAddToCart((currentAddToCart) => ({
        ...product,
        amount: currentAddToCart?.amount ?? 1,
        added: [],
        removed: [],
        price: nextPrice,
        singlePrice: nextPrice,
        isCombo: false,
      }));
      return;
    }

    setOldPrice(undefined);
    setPrice(Number(product?.price));
    setAddToCart((currentAddToCart) => ({
      ...product,
      amount: currentAddToCart?.amount ?? 1,
      price: product?.price,
      singlePrice: product?.price,
      isCombo: true,
    }));
  }, [isCombo, product]);

  useEffect(() => {
    setAddToCart((currentAddToCart) => ({
      ...currentAddToCart,
      amount,
    }));
  }, [amount]);

  useEffect(() => {
    if (Number.isNaN(cartUnitPrice)) return;

    setAddToCart((currentAddToCart) => ({
      ...currentAddToCart,
      chosenWeight: hasWeightOptions ? selectedWeight : null,
      price: cartUnitPrice,
      singlePrice: cartUnitPrice,
    }));
  }, [cartUnitPrice, hasWeightOptions, selectedWeight]);

  return (
    <div className="product-overview">
      <div className="product-info">
        <div className="main-info">
          {window.screen.width >= 767 && (
            <div
              className="desktop back"
              onClick={() => {
                subcategoryId !== undefined
                  ? navigate(
                      `/menu/category/${categoryId}/subcategory/${subcategoryId}`,
                    )
                  : categoryId !== undefined
                    ? navigate(`/menu/category/${categoryId}`)
                    : navigate(
                        `/menu/category/${localStorage.getItem("activeMenu")}`,
                      );
              }}
            >
              <div className="icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="23"
                  height="8"
                  viewBox="0 0 23 8"
                  fill="none"
                >
                  <path
                    d="M5.83337 4H22.1667"
                    stroke="#343434"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M0.357288 3.78563L4.62174 1.22696C5.15496 0.907026 5.83333 1.29112 5.83333 1.91295V6.08705C5.83333 6.70888 5.15496 7.09297 4.62174 6.77304L0.357289 4.21437C0.195454 4.11727 0.195454 3.88273 0.357288 3.78563Z"
                    fill="#343434"
                  />
                </svg>
              </div>
              <p>Назад</p>
            </div>
          )}
          <div className="title">
            <p>
              {product.name}
              <span> {product?.weight !== null && `${product.weight}г`}</span>
            </p>
          </div>
          <div className="price">
            {product?.promoPrice === null || isCombo ? (
              <p>
                {cartUnitPrice.toFixed(2)}€/
                {priceBGN !== null ? priceBGN + t("lv") : "Converting.../"}
              </p>
            ) : (
              <>
                <p className="new">{cartUnitPrice.toFixed(2)}€</p>
                <p className="old">
                  {applyChosenWeight(oldPrice).toFixed(2)}€
                </p>
              </>
            )}
          </div>
          {hasWeightOptions && (
            <div className="price-per-100g">
              <p>{baseUnitPrice.toFixed(2)}€/100г</p>
            </div>
          )}
          <div className="description">
            <p>{product.description}</p>
          </div>
          {weightOptions?.length > 0 && (
            <>
              <div className="weight-label">
                <label>Грамаж</label>
              </div>
              <div className={`input`}>
                <div
                  // ref={weightsRef}
                  className={
                    weightDropdown ? "select active weights" : "select"
                  }
                  onClick={() => {
                    setWeightDropdown(!weightDropdown);
                  }}
                >
                  <p>{selectedWeight || `Грамаж`}</p>
                  <div className="arrow-down"></div>
                </div>
                {weightDropdown && (
                  <div className="select-dropdown weights">
                    {weightOptions?.map((weight) => {
                      return (
                        <div
                          className={"checkbox-wrap"}
                          onClick={() => {
                            setSelectedWeight(weight);
                            setWeightDropdown(false);
                          }}
                        >
                          <input
                            name={weight}
                            type="checkbox"
                            onChange={() => {
                              setSelectedWeight(weight);
                              setWeightDropdown(false);
                            }}
                            checked={selectedWeight === weight}
                          />
                          <label for={weight}>
                            {String(weight).toLowerCase()}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {(product?.optionalProducts?.length > 0 ||
          removableProducts?.length > 0) && (
          <div className="add-and-remove">
            {product?.optionalProducts?.length > 0 && (
              <div className="add-to-product">
                <div className="heading">{t("add")}</div>
                <div className="items">
                  {product?.optionalProducts?.map((add) => {
                    return (
                      <Optional
                        type={"add"}
                        product={product}
                        optional={add}
                        price={price}
                        setPrice={setPrice}
                        addToCart={addToCart}
                        setAddToCart={setAddToCart}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            {removableProducts?.length > 0 && (
              <div className="remove-from-product">
                <div className="heading">{t("without")}</div>
                <div className="items">
                  {removableProducts?.map((remove) => {
                    return (
                      <Optional
                        type={"remove"}
                        product={product}
                        optional={remove}
                        price={price}
                        setPrice={setPrice}
                        addToCart={addToCart}
                        setAddToCart={setAddToCart}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="add-to-cart">
          {/* Meals sold by weight are ordered by the chosen grammage only, so
              the quantity stepper would only confuse the customer. */}
          {!hasWeightOptions && (
            <div className="set-amount">
              <button
                className="minus"
                onClick={() => {
                  amount !== 1 && setAmount(amount - 1);
                }}
              ></button>
              <div className="amount">
                <p>{amount}</p>
              </div>
              <button
                className="plus"
                onClick={() => {
                  setAmount(amount + 1);
                }}
              ></button>
            </div>
          )}
          <div
            className={`add-to-cart-button ${
              !isCategoryValid ? "disabled" : ""
            }`}
          >
            <button
              onClick={() => {
                if (typeof window.gtag === "function") {
                  window.gtag("event", "add_to_cart", {
                    currency: "EUR",
                    value: cartUnitPrice * amount,
                    items: [product],
                  });
                } else {
                  console.error("gtag is not defined!");
                }
                setShowMessage(true);
                const existing = cart.some((item) =>
                  isSameCartItem(item, addToCart),
                );
                if (existing) {
                  setCart((currentCart) =>
                    currentCart.map((item) =>
                      isSameCartItem(item, addToCart)
                        ? { ...item, amount: item.amount + amount }
                        : item,
                    ),
                  );
                } else {
                  setCart([...cart, addToCart]);
                }
              }}
              disabled={!isCategoryValid}
              onMouseOver={() => !isCategoryValid && setShowTooltip(true)}
              onMouseLeave={() => !isCategoryValid && setShowTooltip(false)}
            >
              {t("add")}
            </button>
            {!isCategoryValid && (
              <p className={`${showTooltip ? "visible" : ""} sushi-available`}>
                {t("sushi-available")}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="product-gallery">
        {window.screen.width <= 767 && (
          <div
            className="back"
            onClick={() => {
              subcategoryId !== undefined
                ? navigate(
                    `/menu/category/${categoryId}/subcategory/${subcategoryId}`,
                  )
                : navigate(`/menu/category/${categoryId}`);
            }}
          >
            <div className="icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="8"
                viewBox="0 0 23 8"
                fill="none"
              >
                <path
                  d="M5.83337 4H22.1667"
                  stroke="#343434"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M0.357288 3.78563L4.62174 1.22696C5.15496 0.907026 5.83333 1.29112 5.83333 1.91295V6.08705C5.83333 6.70888 5.15496 7.09297 4.62174 6.77304L0.357289 4.21437C0.195454 4.11727 0.195454 3.88273 0.357288 3.78563Z"
                  fill="#343434"
                />
              </svg>
            </div>
          </div>
        )}
        <div className="gallery">
          <Carousel
            nextIcon={
              product?.images?.length > 1 && (
                <button className="gallery-forward"></button>
              )
            }
            prevIcon={
              product?.images?.length > 1 && (
                <button className="gallery-back"></button>
              )
            }
            fade
            interval={null}
          >
            {product?.images?.length > 0 ? (
              product?.images?.map((image) => {
                return (
                  <Carousel.Item>
                    <img src={image.url} alt=""></img>
                  </Carousel.Item>
                );
              })
            ) : (
              <Carousel.Item>
                <img
                  src="https://alexfish-static.s3.eu-north-1.amazonaws.com/category_sushi.png"
                  alt=""
                ></img>
              </Carousel.Item>
            )}
          </Carousel>
        </div>
        <div className="gallery-nav"></div>
      </div>
    </div>
  );
}
